import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, GlassCard, Badge, Button, Modal } from "@coremonit/design-system";
import { CheckCircle2, XCircle, ExternalLink, Maximize2 } from "lucide-react";
import { useDeploymentTimeline } from "../api/hooks";
import { DeploymentViewer } from "./DeploymentViewer";
import type { DeploymentTimelineEntry } from "@coremonit/types";

interface Props {
  serviceId: string;
  serviceName?: string;
}

export function DeploymentTimelinePanel({ serviceId, serviceName }: Props) {
  const timeline = useDeploymentTimeline(serviceId);
  const nav = useNavigate();
  const [modalEntry, setModalEntry] = useState<DeploymentTimelineEntry | null>(null);

  const items = timeline.data?.items ?? [];

  const successRate = items.length
    ? Math.round((items.filter((e) => e.success).length / items.length) * 100)
    : 0;

  const openFullPage = (entry: DeploymentTimelineEntry, newTab = false) => {
    const path = `/services/${serviceId}/deployments/${entry.id}`;
    if (newTab) window.open(path, "_blank", "noopener,noreferrer");
    else nav(path);
    setModalEntry(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        <StatCard label="Deployments" value={String(items.length)} />
        <StatCard label="Success rate" value={`${successRate}%`} color={successRate >= 90 ? C.green : C.amber} />
        <StatCard label="Avg duration" value={items.length ? `${Math.round(items.reduce((s, e) => s + e.durationSec, 0) / items.length)}s` : "—"} />
        <StatCard label="Latest strategy" value={items[0]?.strategy.replace("_", " ") ?? "—"} />
      </div>

      {timeline.isLoading ? (
        <GlassCard style={{ padding: 40, color: C.textMuted, textAlign: "center" }}>Loading timeline…</GlassCard>
      ) : (
        <GlassCard style={{ padding: 14 }}>
          <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>
            Deployment history {serviceName ? `· ${serviceName}` : ""}
          </div>
          <div style={{ color: C.textMuted, fontSize: 10, marginBottom: 10 }}>
            Click any deployment to inspect rollout details, errors, resource tree, and pod logs — Argo CD style.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setModalEntry(e)}
                style={{
                  textAlign: "left", padding: 10, borderRadius: 10, cursor: "pointer",
                  background: modalEntry?.id === e.id ? C.blueDim : C.surfaceHigh,
                  border: `1px solid ${modalEntry?.id === e.id ? C.borderBright : C.border}`,
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={(ev) => { if (modalEntry?.id !== e.id) (ev.currentTarget as HTMLButtonElement).style.borderColor = C.borderBright; }}
                onMouseLeave={(ev) => { if (modalEntry?.id !== e.id) (ev.currentTarget as HTMLButtonElement).style.borderColor = C.border; }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{e.revision} · {e.imageTag}</div>
                    <div style={{ color: C.textMuted, fontSize: 10 }}>
                      {new Date(e.startedAt).toLocaleString()} · {e.durationSec}s · {e.podsReplaced} pods · {e.strategy.replace("_", " ")}
                    </div>
                    {!e.success && e.failureReason && (
                      <div style={{ color: C.red, fontSize: 10, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>
                        {e.failureReason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {e.success ? <CheckCircle2 size={14} color={C.green} /> : <XCircle size={14} color={C.red} />}
                    <Badge color={e.success ? "green" : "red"}>{e.success ? "success" : "failed"}</Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </GlassCard>
      )}

      {modalEntry && (
        <Modal
          open
          onClose={() => setModalEntry(null)}
          size="xl"
          title={`Deployment · ${modalEntry.revision}`}
          subtitle={`${modalEntry.serviceName} · ${modalEntry.imageTag} · ${modalEntry.strategy.replace("_", " ")}`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setModalEntry(null)}>Close</Button>
              <Button variant="secondary" icon={<ExternalLink size={12} />} onClick={() => openFullPage(modalEntry, true)}>
                Open in new tab
              </Button>
              <Button variant="glow" icon={<Maximize2 size={12} />} onClick={() => openFullPage(modalEntry)}>
                Open full page
              </Button>
            </>
          }
        >
          <DeploymentViewer entry={modalEntry} compact />
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, color = C.text }: { label: string; value: string; color?: string }) {
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color, fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </GlassCard>
  );
}
